import { z } from 'zod'
import { fontFamilySchema } from './fonts'
import { TEMPLATE_ASSET_PATH_PATTERN } from './template-asset'
import { hexColorSchema } from './template-primitives'

/**
 * The masthead — a treatment, never a brand. Colour and typeface come from the ACCOUNT at render
 * time (`accounts.brand_accent`/`brand_font`), which is what lets one template serve several
 * accounts without carrying the wrong brand into someone else's email. The template decides
 * whether to show a logo; the account decides what the logo is.
 */
/**
 * The page and card around the blocks.
 *
 * Hardcoded until now, which was fine while every template was a clip list on a white card. The
 * Trade Desk's `emerald` is not: #fafafa page, a 600px unframed card, and no masthead bar at all —
 * its wordmark is its own block, because Outlook blocks externally-fetched images and a hosted
 * logo does not render there. None of that was reachable, so the shell became a template decision.
 *
 * Every field is optional and absent means "what the renderer already did", so documents written
 * before this keep rendering byte-for-byte.
 */
/**
 * What a template produces. Email is the default and the only one that existed.
 *
 * A separate axis from the layout, because the two targets are not variations of one design — a
 * Google Doc has no cards, no background colours and no 600px card, and pretending a template
 * serves both would mean every email decision quietly meaning something else in a doc. Choosing
 * the target up front is what lets the editor show only the controls that do anything.
 */
export const templateTargetSchema = z.enum(['email', 'googleDoc'])
export type TemplateTarget = z.infer<typeof templateTargetSchema>

/**
 * One named subtab, and what routes an article into it.
 *
 * `schmidt-by-program` is the reason this exists: its document is a tab per research centre and a
 * subtab per programme, and an article lands in a programme when the programme's name appears in
 * it. In the admin portal that mapping is a constant in a client-specific file
 * (`SCHMIDT_CENTER_PROGRAM_MAPPING`), which is why adding a programme is a deploy. Here it is
 * template config: data an admin edits, in the row the template already lives in.
 */
export const docSubtabSchema = z.object({
  title: z.string().min(1).max(120),
  /**
   * Case-insensitive terms. An article joins the subtab whose LONGEST term appears in its headline,
   * summary or snippets — the earlier subtab on a tie. Longest rather than first is the admin
   * portal's rule, and the one that stays right when one programme's name contains another's.
   */
  match: z.array(z.string().min(1).max(120)).max(50),
})
export type DocSubtab = z.infer<typeof docSubtabSchema>

/**
 * One tab of a Google Doc, which is a single-page template plus the slice of the report it shows.
 *
 * Composition rather than partition, and the distinction matters. An earlier cut of this described
 * ONE document cut into tabs by rules — group by header, pin these, rename those. That put a
 * second, parallel layout language next to the template model: an admin who already knew how to
 * build a template still had to learn a tab dialect to control what a tab looked like.
 *
 * A tab IS a template. It is formatted by one, translated through the same flow the preview shows,
 * and the doc is the ordered list of them. Ordering is the list's order, renaming is the tab's
 * title, and a tab that must always exist is simply one that is in the list — three settings that
 * stopped needing to exist.
 */
export const docTabSchema = z.object({
  title: z.string().min(1).max(120),
  /**
   * The email template that formats this tab. Null means the REPORT's own email template — what a
   * tab uses before anyone has chosen otherwise, and what makes a doc template reusable across
   * reports that each keep their own look (CLI-925).
   *
   * An id rather than an inline copy, so fixing the article layout once fixes every tab using it —
   * the thing 56 near-identical template files exist because nobody could do.
   */
  templateId: z.string().nullable().default(null),
  /**
   * Report headers whose articles feed this tab, by name. Empty means every header — the right
   * default for a doc that is one tab, and an explicit choice for one that is many. Two tabs with
   * subtabs may read one header (Schmidt's Observatory tab beside Astrophysics & Space): a story
   * goes to whichever tab's subtab claims it, and the unclaimed to the FIRST such tab's General.
   */
  sections: z.array(z.string().min(1).max(200)).max(50).default([]),
  /**
   * A second level inside this tab, matched by term. Schmidt's programmes: an article joins the
   * subtab whose longest term it mentions.
   */
  subtabs: z.array(docSubtabSchema).max(40).default([]),
  /** Where articles matching no subtab go. Named, because "the rest" is a real category. */
  unmatchedSubtabTitle: z.string().max(120).default('General'),
  /**
   * Drop this tab (and its empty subtabs and General) when it has nothing. Off keeps every one —
   * Schmidt's document has a tab per programme whether or not it was covered this time.
   */
  skipEmpty: z.boolean().default(true),
})
export type DocTab = z.infer<typeof docTabSchema>

/** A Google Doc's structure: the tabs, in the order they appear. */
export const docTabsSchema = z.object({
  tabs: z.array(docTabSchema).max(40).default([]),
})
export type DocTabs = z.infer<typeof docTabsSchema>

/**
 * How the greeting, intro, any other text block and the sign-off read (CLI-793). Absent fields keep
 * the renderer's 14px in the template's master font, so every document written before this existed
 * renders the bytes it always did. `fontFamily` and `color` here are overrides of the master font
 * and colour, for a template whose prose reads differently from its coverage.
 */
export const proseStyleSchema = z.object({
  color: hexColorSchema.optional(),
  fontFamily: fontFamilySchema.optional(),
  sizePx: z.number().int().min(10).max(24).optional(),
  /** Unitless. Absent keeps the renderer's 1.6; the legacy default's "normal" is about 1.15. */
  lineHeight: z.number().min(1).max(2).optional(),
})
export type ProseStyle = z.infer<typeof proseStyleSchema>

export const templateShellSchema = z.object({
  /** Behind the card. */
  pageBackground: hexColorSchema.optional(),
  /**
   * The card's inner padding. Absent keeps the renderer's own, which suits a clip list and is
   * wrong for a design whose blocks bring their own margins — TTD's rows pad themselves, so the
   * default padded them twice.
   */
  bodyPaddingPx: z.number().int().min(0).max(64).optional(),
  /** The card itself. */
  cardBackground: hexColorSchema.optional(),
  cardWidthPx: z.number().int().min(480).max(800).optional(),
  /**
   * Full-bleed: the content runs the width of the reading pane instead of sitting in a card of
   * `cardWidthPx` (CLI-838). The legacy portal's default had no container at all — its text began
   * at the inbox's own edge — and a card centred in a wide pane was the first thing a client
   * noticed about the standard layout. Absent keeps the card, as every email had.
   */
  fullWidth: z.boolean().optional(),
  /**
   * Where a fixed-width card sits in a pane wider than it. Absent centres it, which is what every
   * email did before this existed; ignored when `fullWidth`, which has nothing to place.
   */
  cardAlign: z.enum(['left', 'center']).optional(),
  /**
   * The page's own padding around the card — the `<body>`'s — on every side. Absent keeps the
   * renderer's 24px above and below, 12px at the sides; `0` puts the card flush against the pane.
   */
  outerPaddingPx: z.number().int().min(0).max(64).optional(),
  /** The border and rounded corners. `false` is a flat card, which is what a designed email wants. */
  framed: z.boolean().optional(),
  /**
   * The brand bar. `false` for a template that draws its own header — without it, a designed email
   * gets an accent-coloured strip above the design it was supposed to replace.
   */
  showMasthead: z.boolean().optional(),
  /**
   * This template's own accent, replacing the account's.
   *
   * A deliberate opt-out of the rule that makes templates shareable — colour normally comes from
   * the account so one template serves every client in their own brand. A designed email cannot
   * work that way: `emerald`'s blue is not a highlight colour, it is the design, and rendering it
   * in some other client's brand would produce neither template. So this exists, and setting it is
   * a statement that the template is for one client.
   *
   * Absent is the shareable default, which is what every other template should be.
   */
  accent: hexColorSchema.optional(),
  /**
   * The hidden line an inbox shows beside the subject. Absent means none, which is what every
   * template did before; email clients otherwise scrape the first visible text, and for a template
   * with no salutation that is the first outlet name.
   */
  preheader: z.string().max(200).optional(),
  /**
   * The template's master font: set once on the whole email, so every part inherits it —
   * generated rows, hand-written rows, prose, custom sections. A part that needs another face
   * overrides it where it lives (the Text card, a header element's style, a section's own HTML).
   * Absent follows the account's brand typeface, which is what every template written before
   * this did.
   */
  font: fontFamilySchema.optional(),
  /** The master text colour, inherited the same way. Absent keeps the renderer's #1c1e2a. */
  textColor: hexColorSchema.optional(),
  prose: proseStyleSchema.optional(),
})
export type TemplateShell = z.infer<typeof templateShellSchema>

export const templateMastheadSchema = z.object({
  /**
   * Plain text; `{{token}}` merge syntax is resolved at render time. Empty is allowed and means
   * the logo stands alone — a designed header is often just the client's mark.
   */
  text: z.string().max(200),
  showLogo: z.boolean(),
  /**
   * The bar's own colour, or `transparent` for no bar at all.
   *
   * Separate from `shell.accent` on purpose. The bar and the accent are different decisions: the
   * accent tints section rules and link text throughout the email, and someone changing "the
   * colour of that bar at the top" does not expect every headline to change with it. That is
   * exactly what happened when one field did both.
   *
   * Absent means the accent, which means the account's brand unless the template overrides it —
   * so a template that says nothing still renders in each client's colour. `transparent` is how
   * a logo sits directly on the card, the way The Trade Desk's header does: the masthead stops
   * being a coloured strip and becomes the top of the page.
   */
  background: z.union([hexColorSchema, z.literal('transparent')]).optional(),
  /**
   * The text's colour. Absent means white on a coloured bar and the body ink on a clear one —
   * whichever reads, so turning the bar clear never leaves white text on a white card.
   */
  textColor: hexColorSchema.optional(),
  /** The text's size in px. Absent means the 15px the bar has always used. */
  textSizePx: z.number().int().min(10).max(48).optional(),
  /**
   * Uppercase with letter-spacing — the original treatment, and the default. Off renders the text
   * as typed, which a wordmark set as text ("The Trade Desk") needs.
   */
  uppercase: z.boolean().optional(),
  /**
   * A logo image instead of the Clipbook wordmark.
   *
   * Either a public https URL or the path of an image uploaded through the template editor
   * (`/api/public/assets/<id>`, served open by design). A mail client fetches it from the
   * recipient's machine, so anything behind our auth, or a `cid:` we are not attaching, arrives as
   * a broken image. Absent means the wordmark when `showLogo` is on, and nothing when it is off.
   *
   * Bear in mind Outlook blocks externally-fetched images by default — the reason `emerald` sets
   * its brand as text. A logo here is a nice-to-have, not something a design should depend on.
   */
  logoUrl: z
    .string()
    .max(500)
    .refine(
      (url) => url === '' || /^https:\/\//i.test(url) || TEMPLATE_ASSET_PATH_PATTERN.test(url),
      { message: 'Must be a public https:// address or an uploaded image' },
    )
    .optional(),
  /** Height in px — the one scale control, since width follows the image's own proportions. */
  logoHeightPx: z.number().int().min(12).max(200).optional(),
  /**
   * The uploaded logo's width ÷ height, recorded when it is uploaded (CLI-793). Outlook on Windows
   * sizes an image by its HTML width/height attributes and ignores CSS, so with a height alone a
   * large upload can render at its full pixel size; knowing the ratio lets the email carry a width
   * that matches the chosen height. Absent for a hosted image — its size isn't known here.
   */
  logoAspectRatio: z.number().positive().max(50).optional(),
  /** Where the logo sits across the bar. Absent means right, where the wordmark has always been. */
  logoAlign: z.enum(['left', 'center', 'right']).optional(),
  /**
   * `beside` keeps logo and text on one line; `above` stacks the logo over the text, which is the
   * shape of most letterheads and what a tall logo needs.
   */
  logoLayout: z.enum(['beside', 'above']).optional(),
})
export type TemplateMasthead = z.infer<typeof templateMastheadSchema>
